import React, { useMemo, useState, useEffect, useRef } from "react";
import styles from "./WeeklyVolumeChart.module.css";
import { toLocalDate, startOfWeek, formatWeekLabel } from "../utils/date";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

function getDuration(c) {
  return (c.duration_minutes ?? c.duration ?? c.actual_duration ?? c.duracao ?? 0) || 0;
}

function formatDurationShort(totalMinutes) {
  const m = Math.max(0, Math.round(Number(totalMinutes || 0)));
  if (m === 0) return "";
  if (m >= 60) return `${(m / 60).toFixed(1)}h`;
  return `${m}min`;
}

function formatDurationFull(totalMinutes) {
  const m = Math.round(totalMinutes ?? 0);
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const r = m % 60;
    return r > 0 ? `${h}h ${r}min` : `${h}h`;
  }
  return `${m} min`;
}

function localKey(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

export default function WeeklyVolumeChart({
  checkins = [],
  weeks = 8,
  weeklyGoal = 3,
  cadenceSeries = null,
  durationSeries = null,
}) {
  const data = useMemo(() => {
    const now = new Date();
    const base = startOfWeek(now);
    const buckets = [];

    for (let i = weeks - 1; i >= 0; i--) {
      const wk = new Date(base);
      wk.setDate(base.getDate() - i * 7);
      const key = localKey(wk);
      buckets.push({ key, date: new Date(wk), count: 0, minutes: 0 });
    }

    const byKey = Object.fromEntries(buckets.map((b) => [b.key, b]));

    // Fill count
    if (Array.isArray(cadenceSeries) && cadenceSeries.length) {
      for (const s of cadenceSeries) {
        const dt = toLocalDate(s.date);
        const wk = startOfWeek(dt);
        const key = s.key || localKey(wk);
        if (byKey[key]) byKey[key].count += Number(s.count || 0);
      }
    } else {
      for (const c of checkins) {
        const dt = toLocalDate(c.date || c.created_at || c.checkin_at || c.day || Date.now());
        const wk = startOfWeek(dt);
        const key = localKey(wk);
        if (byKey[key]) byKey[key].count += 1;
      }
    }

    // Fill minutes
    if (Array.isArray(durationSeries) && durationSeries.length) {
      for (const s of durationSeries) {
        const dt = toLocalDate(s.date);
        const wk = startOfWeek(dt);
        const key = s.key || localKey(wk);
        if (byKey[key]) byKey[key].minutes += Number(s.minutes || 0);
      }
    } else {
      for (const c of checkins) {
        const dt = toLocalDate(c.date || c.created_at || c.checkin_at || c.day || Date.now());
        const wk = startOfWeek(dt);
        const key = localKey(wk);
        if (byKey[key]) byKey[key].minutes += getDuration(c);
      }
    }

    return buckets;
  }, [checkins, weeks, cadenceSeries, durationSeries]);

  const maxCount = Math.max(1, ...data.map((d) => d.count));
  const topValue = Math.max(...data.map((d) => d.count), 0);
  const [animatedHeights, setAnimatedHeights] = useState(() => data.map(() => 0));
  const [activeIdx, setActiveIdx] = useState(null);

  useEffect(() => {
    const targets = data.map((d) => (d.count / (maxCount || 1)) * 100);
    let id1, id2;
    id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setAnimatedHeights(targets));
    });
    return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2); };
  }, [data, maxCount]);

  const chartScrollRef = useRef(null);
  const [scrollable, setScrollable] = useState(false);
  useEffect(() => {
    const el = chartScrollRef.current;
    if (!el) return;
    const check = () => setScrollable(el.scrollWidth > el.clientWidth + 4);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [data]);

  // Começa mostrando as semanas mais recentes (fim do scroll) — o usuário
  // arrasta para a esquerda pra ver as semanas anteriores.
  useEffect(() => {
    const el = chartScrollRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth;
  }, [data]);

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <div>
          <div className={styles.title}>Volume semanal</div>
          <div className={styles.sub}>Duração média · meta {weeklyGoal}×/sem</div>
        </div>
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={styles.legendDotGood} />
            Meta batida
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendDotMiss} />
            Abaixo
          </span>
        </div>
      </div>

      <div className={styles.chartScroll}>
      <div className={styles.chart} ref={chartScrollRef}>
        {data.map((d, idx) => {
          const ratioPct = (d.count / (maxCount || 1)) * 100;
          const hitGoal = d.count >= weeklyGoal;
          const isTop = d.count > 0 && d.count === topValue;
          const isActive = idx === activeIdx;
          const hasData = d.count > 0;

          const barClasses = [styles.bar];
          if (d.count > 0) barClasses.push(styles.barNonZero);
          if (hitGoal) barClasses.push(styles.barGood);
          if (isTop) barClasses.push(styles.barTop);

          const tooltipText = hasData
            ? `${d.count} sessão(ões) · ${formatDurationFull(d.minutes)}`
            : "Sem treinos";

          return (
            <div key={d.key} className={styles.col}>
              <div
                className={[styles.barWrap, hitGoal ? styles.barWrapGoal : ""].filter(Boolean).join(" ")}
                title={tooltipText}
                role="button"
                tabIndex={0}
                aria-label={`Semana ${formatWeekLabel(d.date)}: ${tooltipText}`}
                onClick={() => setActiveIdx(isActive ? null : idx)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setActiveIdx(isActive ? null : idx);
                }}
              >
                {hasData && (
                  <span className={styles.valueTag}>
                    <span className={styles.valueRow}>
                      <span className={styles.valueCount}>{d.count}</span>
                      {hitGoal && <span className={styles.goalCheck}><FontAwesomeIcon icon={faCheck} /></span>}
                    </span>
                    <span className={styles.valueDur}>{d.minutes > 0 ? formatDurationShort(d.minutes) : "–"}</span>
                  </span>
                )}
                <div
                  className={barClasses.join(" ")}
                  style={{ height: `${Math.max(0, Math.min(ratioPct, animatedHeights[idx] ?? ratioPct))}%` }}
                />
                <div className={[
                  styles.tooltip,
                  isActive ? styles.tooltipVisible : "",
                  idx === 0 ? styles.tooltipLeft : "",
                  idx === data.length - 1 ? styles.tooltipRight : "",
                ].filter(Boolean).join(" ")}>{tooltipText}</div>
              </div>
              <div className={styles.label}>{formatWeekLabel(d.date)}</div>
            </div>
          );
        })}
      </div>
      {scrollable && <div className={styles.scrollFade} aria-hidden="true" />}
      </div>
      {scrollable && <div className={styles.scrollHint}>← arraste para ver semanas anteriores</div>}
    </div>
  );
}
